import { useMemo, type CSSProperties } from 'react';
import { addDays, schoolYearOf, weekDates, weekOf } from '../../core/calendar';
import { defaultMonth, firstOf, monthMondays, monthOf, nextMonth, sameMonth, stepMonth, stepWeek, type YearMonth } from '../../core/month';
import { yearView, type YearView } from '../../core/planbook';
import type { IsoDate } from '../../core/types';
import type { GetToken } from '../api';
import { navigate, planbookHref, type PlanbookAt, type Zoom } from '../navigation';
import { unitColor } from '../unit-color';
import { itemsOn, planRows, rowTitle, RowLabel, useRangeItems, WeekGrid, WeekLevel, type ItemsByDay, type PlanRow, type RunForPlan, type Track, type WeekPlan } from './PlanbookWeek';
import { day, plural, type EditRequest } from './UnitRow';

const ZOOMS: { zoom: Zoom; text: string }[] = [{ zoom: 'year', text: 'Year' }, { zoom: 'month', text: 'Month' }, { zoom: 'week', text: 'Week' }];
const monthName = (m: YearMonth, style: 'long' | 'short' = 'long') =>
  new Date(`${firstOf(m)}T00:00:00Z`).toLocaleDateString('en-US', style === 'long' ? { month: 'long', year: 'numeric', timeZone: 'UTC' } : { month: 'short', timeZone: 'UTC' });
const ms = (date: IsoDate) => Date.parse(`${date}T00:00:00Z`);

/** The month a level shows: Month's own, the month of Week's Monday, or `fallback` from the year. */
const monthFor = (at: PlanbookAt, fallback: YearMonth): YearMonth => {
  if (at.zoom === 'month') return at.month;
  return at.zoom === 'week' ? monthOf(at.monday) : fallback;
};
/** The week Week opens on: its own, this week when Month's month has it else that month's first, or this week from the year. */
const mondayFor = (at: PlanbookAt, thisWeek: IsoDate): IsoDate => {
  if (at.zoom === 'week') return at.monday;
  if (at.zoom === 'year') return thisWeek;
  const weeks = monthMondays(at.month);
  return weeks.includes(thisWeek) ? thisWeek : weeks[0]!;
};

/**
 * The planbook (R1 to R8, KTD4 to KTD7): the year as unit blocks, a month as its stacked weeks,
 * or one week full size, chosen by the route. Breadcrumbs and a Year / Month / Week control move
 * between levels, and Previous and Next step a month or a week (R9); each move is a navigation, so Back and a reload return to it (R7), and a
 * change of level sends `planbook_zoomed` once (R11).
 */
export function Planbook({ plans, today, at, narrow, getToken, busy, run, collabAttachments = false, onMoveSpan, onTrack }: {
  plans: readonly WeekPlan[]; today: IsoDate; at: PlanbookAt; narrow: boolean; getToken: GetToken; busy: ReadonlySet<string>;
  run: RunForPlan; collabAttachments?: boolean; onMoveSpan(planId: string, edit: EditRequest): Promise<unknown>; onTrack: Track;
}) {
  const year = useMemo(() => yearView(plans, today), [plans, today]);
  // Month opens on today's month when the year has it, else the nearest end of the year.
  const month = monthFor(at, defaultMonth(year.months, today));
  const targets: Record<Zoom, PlanbookAt> = {
    year: { zoom: 'year' },
    month: { zoom: 'month', month },
    week: { zoom: 'week', monday: mondayFor(at, weekOf(today)) },
  };
  const go = (next: PlanbookAt) => {
    if (next.zoom !== at.zoom) onTrack('planbook_zoomed', { zoom: next.zoom, from_zoom: at.zoom });
    navigate(planbookHref(next));
  };
  // Previous and Next at Month and Week (R9), bounded by the year's months; each is a navigation.
  const stepTo = (step: number): PlanbookAt | null => {
    if (at.zoom === 'month') { const m = stepMonth(at.month, step, year.months); return m === null ? null : { zoom: 'month', month: m }; }
    if (at.zoom === 'week') { const monday = stepWeek(at.monday, step, year.months); return monday === null ? null : { zoom: 'week', monday }; }
    return null;
  };
  const step = (direction: 'previous' | 'next') => {
    const next = stepTo(direction === 'next' ? 1 : -1);
    if (!next) return;
    onTrack('planbook_stepped', { zoom: at.zoom, direction });
    navigate(planbookHref(next));
  };
  const firstYear = year.months[0] ? schoolYearOf(firstOf(year.months[0])) : schoolYearOf(today);
  const yearName = `${firstYear}–${String(firstYear + 1).slice(2)}`;

  return <section className="planbook" aria-label="Planbook">
    <div className="planbook-bar">
      {at.zoom !== 'year' && <div className="planbook-step" role="group" aria-label={`Previous or next ${at.zoom}`}>
        <button type="button" className="icon-button" aria-label={`Previous ${at.zoom}`} disabled={!stepTo(-1)} onClick={() => step('previous')}>‹</button>
        <button type="button" className="icon-button" aria-label={`Next ${at.zoom}`} disabled={!stepTo(1)} onClick={() => step('next')}>›</button>
      </div>}
      <nav className="planbook-crumbs" aria-label="Where you are">
        {at.zoom === 'year' ? <span aria-current="page">{yearName}</span> : <button type="button" onClick={() => go(targets.year)}>{yearName}</button>}
        {at.zoom !== 'year' && <><span aria-hidden="true">›</span>
          {at.zoom === 'month' ? <span aria-current="page">{monthName(month)}</span> : <button type="button" onClick={() => go(targets.month)}>{monthName(month)}</button>}</>}
        {at.zoom === 'week' && <><span aria-hidden="true">›</span><span aria-current="page">Week of {day(at.monday)}</span></>}
      </nav>
      <div className="planbook-zoom" role="group" aria-label="Zoom">
        {ZOOMS.map(z => <button key={z.zoom} type="button" aria-pressed={at.zoom === z.zoom} onClick={() => go(targets[z.zoom])}>{z.text}</button>)}
      </div>
    </div>
    <div className="planbook-frame">
      {at.zoom === 'year' && <YearLevel view={year} plans={plans} name={yearName} onMonth={m => go({ zoom: 'month', month: m })} onWeek={monday => go({ zoom: 'week', monday })} />}
      {at.zoom === 'month' && <MonthLevel month={at.month} today={today} plans={plans} narrow={narrow} getToken={getToken} onOpen={monday => go({ zoom: 'week', monday })} />}
      {at.zoom === 'week' && <WeekLevel monday={at.monday} today={today} plans={plans} narrow={narrow} busy={busy} getToken={getToken} run={run} collabAttachments={collabAttachments} onMoveSpan={onMoveSpan} onTrack={onTrack} />}
    </div>
  </section>;
}

/**
 * The Year level (R4): each subject's units as blocks across the teaching year's months, placed by
 * date, with a line for today. A month header opens Month; a block opens the Week it starts in.
 * On a phone it scrolls sideways inside its own container (KTD6).
 */
function YearLevel({ view, plans, name, onMonth, onWeek }: {
  view: YearView; plans: readonly WeekPlan[]; name: string; onMonth(month: YearMonth): void; onWeek(monday: IsoDate): void;
}) {
  const { months, today, rows } = view;
  if (months.length === 0) return <h2 className="planbook-title">{name} teaching year</h2>;
  const start = firstOf(months[0]!);
  const end = firstOf(nextMonth(months.at(-1)!));
  const span = ms(end) - ms(start);
  const at = (date: IsoDate) => ((ms(date) - ms(start)) / span) * 100;
  const between = (from: IsoDate, to: IsoDate) => ({ left: `${at(from)}%`, width: `calc(${at(to) - at(from)}% - 3px)` });
  const showToday = start <= today && today < end;
  return <>
    <h2 className="planbook-title">{name} teaching year</h2>
    <div className="planbook-scroll">
      <div className="planbook-year">
        <div className="planbook-year-row">
          <span />
          <div className="planbook-track planbook-months">
            {months.map(m => <button key={firstOf(m)} type="button" className={`planbook-month${sameMonth(m, monthOf(today)) ? ' now' : ''}`}
              style={between(firstOf(m), firstOf(nextMonth(m)))} aria-label={monthName(m)} onClick={() => onMonth(m)}>{monthName(m, 'short')}</button>)}
          </div>
        </div>
        {rows.map((row, i) => <div key={row.planId} className="planbook-year-row" style={{ '--unit-color': unitColor(plans[i]?.colorIndex ?? i) } as CSSProperties}>
          <div className="planbook-year-label"><span className="cal-swatch" aria-hidden="true" />{row.label}</div>
          <div className="planbook-track">
            {row.blocks.map(b => <button key={b.unitId} type="button" className={`planbook-block${b.done ? ' done' : ''}${b.pastLastDay ? ' over' : ''}`}
              style={between(b.start, addDays(b.end, 1))} title={`${b.name}: ${day(b.start)} to ${day(b.end)}`}
              aria-label={`${b.name}, ${row.label}, ${day(b.start)} to ${day(b.end)}. Open the week it starts`} onClick={() => onWeek(b.opens)}>{b.name}</button>)}
            {showToday && <span className="planbook-today" style={{ left: `${at(today)}%` }} aria-hidden="true" />}
          </div>
        </div>)}
      </div>
    </div>
    <p className="muted planbook-hint">Choose a month to see its weeks, or a unit to open the week it starts. The orange line is today.</p>
  </>;
}

/**
 * The Month level (R5): the month's planbook weeks stacked, each with "Open week", its items
 * read-only from one range request. On a phone each week lists its item count per subject (KTD6).
 */
function MonthLevel({ month, today, plans, narrow, getToken, onOpen }: {
  month: YearMonth; today: IsoDate; plans: readonly WeekPlan[]; narrow: boolean; getToken: GetToken; onOpen(monday: IsoDate): void;
}) {
  const { year: y, month: m } = month;
  const mondays = useMemo(() => monthMondays({ year: y, month: m }), [y, m]);
  const { items, error } = useRangeItems(getToken, mondays[0]!, addDays(mondays.at(-1)!, 4));
  // Every week's rows at once, recomputed only when the plans or the month change.
  const weeks = useMemo(() => mondays.map(monday => ({ monday, rows: planRows(plans, monday) })), [plans, mondays]);
  return <>
    <h2 className="planbook-title">{monthName(month)}</h2>
    {error && <p className="error-message" role="alert">We could not load this month's items. {error}</p>}
    <div className="planbook-weeks">
      {weeks.map(({ monday, rows }) => <MonthWeek key={monday} monday={monday} today={today} rows={rows} items={items} narrow={narrow} onOpen={() => onOpen(monday)} />)}
    </div>
  </>;
}

function MonthWeek({ monday, today, rows, items, narrow, onOpen }: {
  monday: IsoDate; today: IsoDate; rows: readonly PlanRow[]; items: ItemsByDay | null; narrow: boolean; onOpen(): void;
}) {
  const dates = weekDates(monday);
  return <section className={`planbook-week${monday === weekOf(today) ? ' now' : ''}`} aria-label={`Week of ${day(monday)}`}>
    <div className="planbook-week-head">
      <span>Week of {day(monday)}</span>
      <button type="button" className="text-button" onClick={onOpen}>Open week</button>
    </div>
    {narrow
      ? <ul className="planbook-counts ph-no-capture" data-ph-no-capture>
          {rows.map(({ plan, row }) => <li key={row.planId} style={{ '--unit-color': unitColor(plan.colorIndex) } as CSSProperties}>
            <span className="cal-swatch" aria-hidden="true" />
            <span>{items ? `${rowTitle(row)}: ${plural(dates.reduce((n, d) => n + itemsOn(items, row.planId, d).length, 0), 'item')}` : rowTitle(row)}</span>
          </li>)}
        </ul>
      : <WeekGrid monday={monday} dates={dates} today={today} rows={rows}
          label={({ row }) => <RowLabel row={row} />}
          cell={({ row }, d) => {
            const list = itemsOn(items, row.planId, d.date);
            return list.length > 0 && <ul className="planbook-items" aria-label={`${row.label} items`}>
              {list.map(item => <li key={item.id} className="planbook-item">
                <span className="planbook-item-title">{item.title}</span>
                {item.materials.length > 0 && <span className="planbook-item-count">{plural(item.materials.length, 'attachment')}</span>}
              </li>)}
            </ul>;
          }} />}
  </section>;
}
